import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Slider,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';

function Home() {
  const [step, setStep] = useState(() => {
    const savedState = localStorage.getItem('decisionState');
    return savedState ? JSON.parse(savedState).step : 0;
  });
  const [decisionName, setDecisionName] = useState(() => {
    const savedState = localStorage.getItem('decisionState');
    return savedState ? JSON.parse(savedState).decisionName : '';
  });
  const [criteria, setCriteria] = useState(() => {
    const savedState = localStorage.getItem('decisionState');
    return savedState ? JSON.parse(savedState).criteria : [];
  });
  const [options, setOptions] = useState(() => {
    const savedState = localStorage.getItem('decisionState');
    return savedState ? JSON.parse(savedState).options : [];
  });
  const [newCriteria, setNewCriteria] = useState('');
  const [newOption, setNewOption] = useState('');
  const [results, setResults] = useState(() => {
    const savedState = localStorage.getItem('decisionState');
    return savedState ? JSON.parse(savedState).results : [];
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    const stateToSave = {
      step,
      decisionName,
      criteria,
      options,
      results
    };
    localStorage.setItem('decisionState', JSON.stringify(stateToSave));
  }, [step, decisionName, criteria, options, results]);

  const resetAll = () => {
    setStep(0);
    setDecisionName('');
    setCriteria([]);
    setOptions([]);
    setNewCriteria('');
    setNewOption('');
    setResults([]);
    localStorage.removeItem('decisionState');
  };

  const startDecision = () => {
    if (decisionName.trim()) {
      setStep(1);
    }
  };

  const addCriterion = () => {
    if (newCriteria.trim()) {
      setCriteria([...criteria, { name: newCriteria, weight: 50 }]);
      setNewCriteria('');
    }
  };

  const updateCriterionWeight = (index, weight) => {
    const updatedCriteria = [...criteria];
    updatedCriteria[index].weight = weight;
    setCriteria(updatedCriteria);
  };

  const addOption = () => {
    if (newOption.trim()) {
      const ratings = criteria.map(criterion => ({
        criterionName: criterion.name,
        rating: 50
      }));
      setOptions([...options, { name: newOption, ratings }]);
      setNewOption('');
    }
  };

  const updateOptionRating = (optionIndex, criterionIndex, rating) => {
    const updatedOptions = [...options];
    updatedOptions[optionIndex].ratings[criterionIndex].rating = rating;
    setOptions(updatedOptions);
  };

  const calculateResults = () => {
    const calculatedResults = options.map(option => {
      let totalScore = 0;
      let totalWeight = 0;
      const criterionScores = [];

      option.ratings.forEach((rating, index) => {
        const criterion = criteria[index];
        const criterionScore = (rating.rating / 100) * (criterion.weight / 100);
        totalScore += criterionScore;
        totalWeight += criterion.weight / 100;
        criterionScores.push({
          name: criterion.name,
          score: criterionScore,
          weight: criterion.weight,
          color: `hsl(${(index * 360) / criteria.length}, 70%, 50%)`
        });
      });

      return {
        name: option.name,
        score: totalScore / totalWeight,
        criterionScores
      };
    });

    setResults(calculatedResults.sort((a, b) => b.score - a.score));
    setStep(3);
  };

  const deleteCriterion = (index) => {
    const updatedCriteria = criteria.filter((_, i) => i !== index);
    setCriteria(updatedCriteria);
    const updatedOptions = options.map(option => ({
      ...option,
      ratings: option.ratings.filter((_, i) => i !== index)
    }));
    setOptions(updatedOptions);
  };

  const deleteOption = (index) => {
    const updatedOptions = options.filter((_, i) => i !== index);
    setOptions(updatedOptions);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            Decision Maker{decisionName && `: ${decisionName}`}
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Make better decisions by comparing options systematically
          </Typography>
        </Box>
        {step > 0 && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={resetAll}
            sx={{ 
              height: 'fit-content',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
              px: { xs: 1, sm: 2 }
            }}
          >
            Reset
          </Button>
        )}
      </Box>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {['Name Decision', 'Define Criteria', 'Add Options', 'View Results'].map((label, index) => (
          <Step key={label} onClick={() => {
            // Only allow navigation to steps that make sense
            if (index === 0 || // Always allow going back to step 0
                (index === 1 && decisionName.trim()) || // Allow step 1 if decision name is set
                (index === 2 && criteria.length > 0) || // Allow step 2 if criteria exist
                (index === 3 && options.length > 0)) { // Allow step 3 if options exist
              setStep(index);
            }
          }} sx={{ cursor: 'pointer' }}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper elevation={3} sx={{ p: 3 }}>
        {step === 0 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              What decision are you trying to make?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Decision Name"
                value={decisionName}
                onChange={(e) => setDecisionName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && decisionName.trim()) {
                    startDecision();
                  }
                }}
                variant="outlined"
                placeholder="e.g., Choosing a new laptop"
              />
              <Button
                variant="contained"
                onClick={startDecision}
                disabled={!decisionName.trim()}
                endIcon={<ArrowForwardIcon />}
              >
                Start
              </Button>
            </Box>
          </Box>
        )}

        {step === 1 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Step 1: Define Criteria
            </Typography>
            <List>
              {criteria.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ display: 'block', textAlign: 'center', mb: 1, fontWeight: 'medium' }}>
                    How important is this criteria to you?
                  </Typography>
                  <Box sx={{ display: 'flex', width: '100%', pl: '150px', pr: '40px' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption">Not Important</Typography>
                        <Typography variant="caption">Most Important</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
              {criteria.map((criterion, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: { xs: 1, sm: 4 }, pr: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                      <Typography sx={{ minWidth: { xs: '100%', sm: '150px' }, mb: { xs: 1, sm: 0 } }}>{criterion.name}</Typography>
                      <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                        <Slider
                          value={criterion.weight}
                          onChange={(_, value) => updateCriterionWeight(index, value)}
                          valueLabelDisplay="auto"
                          step={10}
                          marks={[
                            { value: 0 },
                            { value: 10 },
                            { value: 20 },
                            { value: 30 },
                            { value: 40 },
                            { value: 50 },
                            { value: 60 },
                            { value: 70 },
                            { value: 80 },
                            { value: 90 },
                            { value: 100 }
                          ]}
                          valueLabelFormat={(value) => `${value}%`}
                          sx={{
                            '& .MuiSlider-mark': {
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              backgroundColor: 'white',
                              border: '2px solid #1976d2',
                              '&.MuiSlider-markActive': {
                                backgroundColor: '#1976d2',
                              },
                            },
                            '& .MuiSlider-thumb': {
                              width: { xs: '32px', sm: '24px' },
                              height: { xs: '32px', sm: '24px' },
                              marginLeft: { xs: '16px', sm: '12px' },
                            },
                            '& .MuiSlider-rail': {
                              height: { xs: '4px', sm: '2px' },
                              backgroundColor: '#e0e0e0',
                            },
                            '& .MuiSlider-track': {
                              height: { xs: '4px', sm: '2px' },
                              backgroundColor: '#1976d2',
                            },
                          }}
                        />
                      </Box>
                      <Typography sx={{ minWidth: { xs: 'auto', sm: '40px' }, textAlign: { xs: 'left', sm: 'right' } }}>{criterion.weight}%</Typography>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => deleteCriterion(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Criterion Name"
                value={newCriteria}
                onChange={(e) => setNewCriteria(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newCriteria.trim()) {
                    addCriterion();
                  }
                }}
                variant="outlined"
              />
              <Button
                variant="contained"
                onClick={addCriterion}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
            {criteria.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={() => setStep(2)}
                  endIcon={<ArrowForwardIcon />}
                >
                  Next: Add Options
                </Button>
              </Box>
            )}
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Step 2: Add Options
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Option Name"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newOption.trim()) {
                    addOption();
                  }
                }}
                variant="outlined"
              />
              <Button
                variant="contained"
                onClick={addOption}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
            <List>
              {options.map((option, optionIndex) => (
                <ListItem key={optionIndex} sx={{ width: '100%', display: 'block', px: 0 }}>
                  <Card sx={{ width: '100%', display: 'flex' }}>
                    <CardContent sx={{ width: '100%', px: 3, py: 2, pr: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                          {option.name}
                        </Typography>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => deleteOption(optionIndex)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      {option.ratings.map((rating, criterionIndex) => (
                        <Box key={criterionIndex} sx={{ mb: 2, width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: '150px' }}>
                              {rating.criterionName}
                            </Typography>
                            <Typography variant="body2" sx={{ minWidth: '40px' }}>
                              {rating.rating}%
                            </Typography>
                          </Box>
                          <Slider
                            value={rating.rating}
                            onChange={(_, value) => updateOptionRating(optionIndex, criterionIndex, value)}
                            valueLabelDisplay="auto"
                            step={25}
                            marks={[
                              { value: 0 },
                              { value: 25 },
                              { value: 50 },
                              { value: 75 },
                              { value: 100 }
                            ]}
                            valueLabelFormat={(value) => `${value}%`}
                            sx={{
                              '& .MuiSlider-mark': {
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                border: '2px solid #1976d2',
                                '&.MuiSlider-markActive': {
                                  backgroundColor: '#1976d2',
                                },
                              },
                              '& .MuiSlider-thumb': {
                                width: { xs: '32px', sm: '24px' },
                                height: { xs: '32px', sm: '24px' },
                                marginLeft: { xs: '16px', sm: '12px' },
                              },
                              '& .MuiSlider-rail': {
                                height: { xs: '4px', sm: '2px' },
                                backgroundColor: '#e0e0e0',
                              },
                              '& .MuiSlider-track': {
                                height: { xs: '4px', sm: '2px' },
                                backgroundColor: '#1976d2',
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </ListItem>
              ))}
            </List>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              {options.length > 0 && (
                <Button
                  variant="contained"
                  onClick={calculateResults}
                  endIcon={<ArrowForwardIcon />}
                >
                  Analyze
                </Button>
              )}
            </Box>
          </Box>
        )}

        {step === 3 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Step 3: Results
            </Typography>
            <List>
              {results.map((result, index) => (
                <ListItem key={index} sx={{ width: '100%', display: 'block', px: 0 }}>
                  <Paper elevation={2} sx={{ p: 3, width: '100%' }}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">{`${index + 1}. ${result.name}`}</Typography>
                        <Typography variant="h6" color="primary">
                          {`${(result.score * 100).toFixed(1)}%`}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        height: '24px', 
                        borderRadius: '4px', 
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.1)',
                        mb: 2,
                        width: `${result.score * 100}%`,
                        maxWidth: '100%'
                      }}>
                        {result.criterionScores.map((criterion, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              width: `${(criterion.score / result.score) * 100}%`,
                              bgcolor: criterion.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.75rem',
                              transition: 'width 0.3s ease'
                            }}
                            title={`${criterion.name}: ${(criterion.score * 100).toFixed(1)}%`}
                          />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {result.criterionScores.map((criterion, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <Box
                              sx={{
                                width: '12px',
                                height: '12px',
                                bgcolor: criterion.color,
                                borderRadius: '2px'
                              }}
                            />
                            <Typography variant="caption">
                              {`${criterion.name}: ${(criterion.score * 100).toFixed(1)}%`}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Paper>
                </ListItem>
              ))}
            </List>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={() => setStep(2)}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default Home; 